import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import UserService from "../../api/UserService";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import UserListComponent from "../../components/UserListComponent/UserListComponent";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { generateUUIDFromID } from "../../services/UUIDGeneratorService";
import { RootState } from "../../store";
import { ROLES } from "../../store/user-slice";
import styles from "../../styles/user-management.module.css";
import { Technician, UsersQueryParams } from "../../types/user";

export const DeactivatedUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [users, setUsers] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const isCustomerAdminFromStore = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );
  const isCustomerAdmin =
    location?.state?.customerAdmin || isCustomerAdminFromStore;
  const selectedCustomer = useSelector(
    (state: RootState) => state.userManagement.selectedCustomer
  );
  const customerId = useSelector(
    (state: RootState) => state.user.currentUser.customerId
  );

  const scrollableWrapper = document.getElementById(
    "scrollable-wrapper"
  ) as HTMLElement;
  const [isFetching, setIsFetching] = useInfiniteScroll(
    scrollableWrapper,
    hasMore
  );

  const fetchDeactivatedUsers = async () => {
    setLoading(true);
    const params: UsersQueryParams = {
      ...(!isCustomerAdmin && {
        "filter[roles][]": [ROLES.ADMIN, ROLES.TECHNICIAN],
      }),
      ...(isCustomerAdmin && {
        "filter[customerId]": generateUUIDFromID(
          selectedCustomer?.id?.toString()!
        ),
      }),
      ...(isCustomerAdminFromStore && { "filter[customerId]": customerId }),
      "page[number]": page,
      "page[size]": 8,
      disabled: "true",
    };

    try {
      const result = isCustomerAdmin
        ? await UserService.GetDeactivatedCustomerUsers(params)
        : await UserService.GetDeactivatedUsers(params);
      if (result.data.length === 0) {
        setHasMore(false);
      } else {
        setUsers((prevUsers) => {
          const newUsers = result.data.filter(
            (user) => !prevUsers.some((prevUser) => prevUser.id === user.id)
          );
          return [...prevUsers, ...newUsers];
        });
      }
    } catch (error) {
      console.error("Error fetching deactivated users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasMore || page === 1) fetchDeactivatedUsers();
  }, [page]);

  useEffect(() => {
    if (isFetching) {
      setPage((prevPage) => prevPage + 1);
      setIsFetching(false);
    }
  }, [isFetching]);

  return (
    <AppLayout
      withBackButton={true}
      onGoBack={() => {
        navigate(-1);
      }}
    >
      <div className={styles["customer-um"]} id="scrollable-wrapper">
        <div className={styles["customer-um__heading"]}>
          <p className={styles["customer-um__title"]}>
            {t("DeactivatedUsers")}
          </p>
        </div>

        {loading && page === 1 ? (
          <LoadingSpinner loading={loading} />
        ) : (
          <UserListComponent
            users={users}
            loading={loading}
            page={page}
            backgroundColor={"#CED3DC"}
            deactivated={true}
            customerAdmin={location?.state?.customerAdmin ?? false}
          />
        )}
      </div>
    </AppLayout>
  );
};
